import { dataLayerConfig, TAGLink } from '@aspendental/shared-components-web';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import router from 'next/router';
import { useEffect, useState } from 'react';
import { mapCreditResponseCodes, mapDentalConditionCodes, mapDentalSolutionCodes } from '../Forms/PatientInformation/PatientInformation.helpers';
import { HydraState, SubmitAppointmentErrorCode } from '@src/constants';
import { submitConsentManagement } from '@src/lib/client';
import { getLeadsScore } from '@src/services/getLeadsScore';
import { submitAppointment } from '@src/services/submitAppointment';
import { updateLead } from '@src/services/updateLead';
import { useAppStore, usePatientInformationStore, useScheduleQuestionsStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
interface StepCTAProps {
  enablePtcSlotFilter: boolean;
}
export default function StepCTAs({
  enablePtcSlotFilter
}: Readonly<StepCTAProps>) {
  const {
    questionsAnswers,
    questions,
    setUnansweredQuestions,
    setScheduleInfoModal,
    formId
  } = useScheduleQuestionsStore(store => store);
  const theme = useTheme();
  const {
    locale
  } = router;
  const languagePreference = locale === 'es-US' ? 'Spanish' : 'English';

  // Lead ID:
  const {
    leadId,
    salesforceS20,
    salesforceGuid,
    salesforceTier,
    setSalesforceS20,
    setSalesforceTier
  } = usePatientInformationStore();
  const {
    chosenCenterData,
    chosenAppointmentData,
    chosenAppointmentKey,
    presetCenterID,
    presetCenterName,
    trackAppointmentTimesShown,
    trackAppointmentDaysShown,
    setNoChosenAppointmentError,
    setLoadingAppointments
  } = useAppointmentInformationStore();
  const {
    currentStep,
    decrementStep,
    incrementStep,
    isStepSubmitting,
    setIsStepSubmitting
  } = useStepManagerStore(store => store);
  const {
    enableHydraCalls,
    sessionId
  } = useAppStore(store => store);
  const [disabled, setDisabled] = useState(true);
  const {
    t
  } = useTranslation('index');
  useEffect(() => {
    if (currentStep === 2 && disabled) {
      setDisabled(false);
    }
  }, [disabled, currentStep]);
  useEffect(() => {
    if (currentStep === 3 && typeof window !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [currentStep]);
  const handleScrollTop = () => {
    if (typeof window !== 'undefined') {
      console.log('✅ Scroll to top initiated');
      console.log('🔄 handleScrollTop called at:', new Date().toISOString());
      console.log('📍 Current scroll position before:', window.scrollY);
      console.log('📋 Current step:', currentStep);
      console.log('🔍 Call stack:', new Error().stack);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    console.log('🚨 useEffect scroll triggered - currentStep changed to:', currentStep);
    console.log('⏰ Timestamp:', new Date().toISOString());
    if (typeof window !== 'undefined') {
      console.log('📍 Current scroll position before useEffect scroll:', window.scrollY);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      console.log('✅ useEffect scroll to top initiated');
    }
  }, [currentStep]);
  const handleBackStep = () => {
    if (isStepSubmitting) return;
    console.log('⬅️ handleBackStep called - about to scroll to top');
    handleScrollTop();
    dataLayerConfig({
      event: `scheduleform_step${currentStep}_clickback`
    });
    decrementStep();
  };
  const handleNextStep = () => {
    setIsStepSubmitting(true);
    if (currentStep === 1) {
      // Check validation first
      const unanswered = questions?.filter(question => {
        if (question.required === 1 || question.required === '1') {
          const answers = questionsAnswers[question.short_name];
          setIsStepSubmitting(false);
          setDisabled(true);
          return !answers || answers.length === 0;
        }
        return false;
      });
      setUnansweredQuestions(unanswered);
      if (unanswered?.length > 0) {
        setTimeout(() => {
          const firstUnansweredQuestion = unanswered[0];
          if (firstUnansweredQuestion) {
            const questionElement = document.getElementById(firstUnansweredQuestion.short_name);
            if (questionElement) {
              const focusableElement = questionElement.closest('[data-test-id="section_schedule_question"]')?.querySelector('input, button, [tabindex]:not([tabindex="-1"])') as HTMLElement;
              if (focusableElement) {
                focusableElement.focus();
                // Removed scrollIntoView - no more scrolling to validation errors
              }
            }
          }
        }, 100);
        return;
      }

      // Only scroll to top if validation passes
      console.log('✅ Step 1 validation passed - about to scroll to top');
      handleScrollTop();
      dataLayerConfig({
        event: 'scheduleform_step1_clickcontinue'
      });
      const dentalConditionCodes = mapDentalConditionCodes(questionsAnswers.current_dental_condition);
      const dentalSolutionCodes = mapDentalSolutionCodes(questionsAnswers.current_dental_solutions);
      const creditResponseValue = questionsAnswers.current_credit_score;
      let creditResponseArray: string[] = [];
      if (Array.isArray(creditResponseValue)) {
        creditResponseArray = creditResponseValue.flat();
      } else if (creditResponseValue) {
        creditResponseArray = [creditResponseValue];
      }
      const creditResponseCodes = mapCreditResponseCodes(creditResponseArray);
      dataLayerConfig({
        event: 'dental_condition_form',
        lead_id: null,
        Q1_current_condition_code: dentalConditionCodes.join(', '),
        Q2_current_dental_solution_code: dentalSolutionCodes.join(', '),
        Q3_current_credit_response_code: creditResponseCodes.join(', '),
        lead_tier: null,
        lead_segment: null,
        website_selected_center_name: presetCenterName,
        website_selected_center_id: presetCenterID,
        language_preference: locale ?? 'en-US'
      });
      setLoadingAppointments(false);
      incrementStep();
      setIsStepSubmitting(false);
      return;
    }
    if (currentStep === 2) {
      dataLayerConfig({
        event: 'scheduleform_step2_clickcontinue'
      });
      setIsStepSubmitting(false);
      setLoadingAppointments(false);
      return;
    }
    if (currentStep === 3) {
      if (!chosenAppointmentData) {
        setIsStepSubmitting(false);
        setNoChosenAppointmentError(true);
        return;
      } else {
        let updatedSegment = salesforceS20;
        let updatedTier = salesforceTier;
        setIsStepSubmitting(true);
        setTimeout(() => {
          // GA4: 2.4
          getLeadsScore({
            leadId,
            salesforceId: salesforceGuid ?? '',
            enableHydra: enableHydraCalls,
            sessionId
          }).then(data => {
            if (data?.score?.lead_segment20) {
              setSalesforceS20(data.score.lead_segment20);
              updatedSegment = data.score.lead_segment20;
            }
            if (data?.score?.tier) {
              setSalesforceTier(`T${data.score.tier}`);
              updatedTier = `T${data.score.tier}`;
            }
          }).catch(async error => {
            console.error('Error getting Leads Score:', error);
          }).finally(() => {
            dataLayerConfig({
              event: 'appointment_step',
              step_name: 'location_date_time',
              lead_id: salesforceGuid,
              lead_tier: updatedSegment && updatedSegment != 21 ? updatedTier : 'T1',
              lead_segment: updatedSegment ?? '21',
              appointments_available: true,
              // < Always true if this block is hit
              website_selected_center_name: presetCenterName,
              website_selected_center_id: presetCenterID,
              appointment_selected_center_name: chosenCenterData?.name,
              appointment_selected_center_id: chosenCenterData?.code,
              language_preference: locale ?? 'en-US',
              ptc: enablePtcSlotFilter ? '1' : '0',
              appointment_time_slots: trackAppointmentTimesShown,
              appointment_days_shown: trackAppointmentDaysShown,
              hydra_status: enableHydraCalls ? HydraState.ON : HydraState.OFF
            });
            scheduleAppointment({
              dataLayerEvent: 'scheduleform_step3_confirmconsultation'
            });
          });
        }, 2500);
        return;
      }
    }
  };
  const scheduleAppointment = ({
    dataLayerEvent
  }) => {
    updateLead({
      leadId,
      language: languagePreference,
      enableHydra: enableHydraCalls,
      sessionId
    });
    dataLayerConfig({
      event: dataLayerEvent
    });
    submitAppointment({
      consultId: chosenAppointmentKey!,
      leadId,
      enableHydra: enableHydraCalls,
      sessionId
    }).then(async data => {
      if (data) {
        // Add consent management call after successful appointment submission
        try {
          const patientData = usePatientInformationStore.getState();
          await submitConsentManagement(formId || 'schedule-consultation', salesforceGuid, {
            Email: patientData.email,
            Mobile: patientData.phone,
            FirstName: patientData.firstName,
            LastName: patientData.lastName,
            DateOfBirth: patientData.dateOfBirth,
            Address: patientData.address,
            City: patientData.city,
            State: patientData.state,
            Zip: patientData.zipCode
          });
          console.log('Consent management submitted successfully');
        } catch (consentError) {
          console.error('Failed to submit consent management:', consentError);
          // Don't block the user flow if consent fails
        }
        incrementStep();
      }
      setIsStepSubmitting(false);
      console.log('📅 Appointment scheduled - about to scroll to top');
      handleScrollTop();
    }).catch(async error => {
      console.error('Error submitting appointment:', error);
      if (error.message === SubmitAppointmentErrorCode.CONSULT_SCHEDULED) {
        setScheduleInfoModal({
          title: 'It looks like you already have a consultation scheduled.',
          description: () => <Typography variant="bodyLargeBook">
								{t('wantToCancelOrReschedule')} <br />
								{t('pleaseCallAt')}
								<TAGLink href={`tel:888-651-9950`} linkSx={{
              color: theme.palette.text.interactive,
              textDecoration: 'none'
            }}>
									{' '}
									888-651-9950{' '}
								</TAGLink>
								{t('forAssistance')}
							</Typography>,
          buttonText: 'Cancel',
          buttonOnClick: () => {
            setScheduleInfoModal(null);
          }
        });
        setIsStepSubmitting(false);
        return;
      } else if (error.message === SubmitAppointmentErrorCode.NO_APPOINTMENTS) {
        setScheduleInfoModal({
          title: 'The appointment you have selected is no longer available.',
          description: () => <Typography variant="bodyLargeBook">{t('chooseAnotherTimeApology')}</Typography>,
          buttonText: 'Cancel',
          buttonOnClick: () => {
            setScheduleInfoModal(null);
          }
        });
        setIsStepSubmitting(false);
        return;
      }
      setIsStepSubmitting(false);
    });
  };
  const getCTAButtonLabel = () => {
    if (isStepSubmitting) return `${t('loading')}`;
    if (currentStep === 3) return `${t('confirmConsultation')}`;
    return `${t('continue')}`;
  };
  return <Stack alignItems="center" justifyContent="center" data-sentry-element="Stack" data-sentry-component="StepCTAs" data-sentry-source-file="StepCTAs.tsx">
			<Stack direction="row" spacing={1.5} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="StepCTAs.tsx">
				{currentStep > 1 && <Button size="M" variant="text" onClick={handleBackStep} disabled={isStepSubmitting}>
						<ChevronLeftIcon color={isStepSubmitting ? 'disabled' : 'primary'} />
						<Typography color={isStepSubmitting ? 'text.disabled' : 'text.primary'} variant="bodyLargeBook">
							{t('back')}
						</Typography>
					</Button>}

				<Button form={formId} type={formId === 'step-2-form' && !disabled ? 'submit' : 'button'} variant="primaryAccent1" data-test-id="schedule-layout-button" size="M" onClick={handleNextStep} id={formId} data-sentry-element="Button" data-sentry-source-file="StepCTAs.tsx">
					{isStepSubmitting && <CircularProgress sx={{
          mr: 1
        }} size={20} />}
					{getCTAButtonLabel()}
				</Button>
			</Stack>
		</Stack>;
}